import { RESOURCE_BY_SKILL, RESOURCES, SKILLS, type SkillId } from './catalog.ts';
import { createInitialActivities, recordContractProgress } from './activities.ts';
import { SAVE_SCHEMA_VERSION, type GameState, type SkillState } from './types.ts';
import { recordProgress } from './progress.ts';

function createSkills(): Record<SkillId, SkillState> {
  return Object.fromEntries(SKILLS.map((id) => [id, {
    id,
    xp: 0,
    level: 1,
    toolTier: 0,
    buildingLevel: 0
  }])) as Record<SkillId, SkillState>;
}

function createResourceMap(): Record<string, number> {
  return Object.fromEntries(RESOURCES.map((resource) => [resource, 0]));
}

export function createInitialState(playerId: string, now = Date.now()): GameState {
  const state: GameState = {
    schemaVersion: SAVE_SCHEMA_VERSION,
    playerId,
    createdAt: now,
    updatedAt: now,
    lastSimulatedAt: now,
    revision: 0,
    run: {
      startedAt: now,
      activeSkill: 'Woodcutting',
      trainingActive: false,
      criticalSequence: 0,
      gold: 0,
      resources: createResourceMap(),
      skills: createSkills(),
      earnedGold: 0,
      earnedResources: createResourceMap(),
      productionValue: 0
    },
    prestige: {
      echoShards: 0,
      lifetimeEchoShards: 0,
      ascensions: 0,
      passiveSkills: [],
      milestones: [],
      achievements: [],
      constellationNodes: [],
      activities: createInitialActivities(now),
      upgrades: {
        eternalCraft: 0,
        echoingKnowledge: 0,
        deepSlumber: 0,
        perfectRecall: 0,
        resonantExchange: 0,
        harmonicBond: 0,
        swiftHands: 0,
        legacyVault: 0,
        twinEchoes: 0,
        beyondTheVeil: 0
      }
    },
    stats: {
      activeSeconds: 0,
      offlineSeconds: 0,
      totalGoldEarned: 0,
      totalResourceUnitsEarned: 0,
      manualActions: 0,
      skillSwitches: 0,
      resourcesSold: 0,
      largestSaleGold: 0,
      toolsPurchased: 0,
      hallsPurchased: 0,
      skillLevelsGained: 0,
      criticalTrainingProcs: 0,
      skillXpEarned: {},
      activeSecondsBySkill: {},
      resourceUnitsEarned: {},
      resourcesSoldByResource: {},
      toolsPurchasedBySkill: {},
      hallsPurchasedBySkill: {},
      skillLevelsGainedBySkill: {},
      surgeTrainingSeconds: 0
    }
  };
  return recordContractProgress(state);
}

export function selectActiveSkill(state: GameState, skill: SkillId, now = Date.now()): GameState {
  if (!state.run.skills[skill]) throw new Error(`Unknown skill: ${skill}`);
  return recordProgress({
    ...state,
    updatedAt: now,
    revision: state.revision + 1,
    run: { ...state.run, activeSkill: skill, trainingActive: true },
    stats: { ...state.stats, skillSwitches: (state.stats.skillSwitches ?? 0) + (state.run.activeSkill === skill ? 0 : 1) }
  }).state;
}

export function isTrainingActive(state: GameState): boolean {
  return state.run.trainingActive !== false;
}

export function resourceForActiveSkill(state: GameState): string {
  return RESOURCE_BY_SKILL[state.run.activeSkill];
}
